import React, { useEffect } from 'react';
import { ActivityIndicator, View, StyleSheet, Platform } from 'react-native';
import { useAuth } from '../contexts/AuthContext';
import { COLORS } from '../constants/config';

// Navigators
import MainNavigator from './MainNavigator';
import AccessDeniedScreen from '../screens/auth/AccessDeniedScreen';

// Key for storing the intended URL in sessionStorage
const INTENDED_URL_KEY = 'sms_intended_url';

// Helper to save intended URL (for redirect after SSO login)
export const saveIntendedUrl = () => {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    const currentPath = window.location.pathname + window.location.search;
    // Don't save root or callback paths
    if (currentPath && currentPath !== '/' && !currentPath.startsWith('/auth/')) {
      try {
        sessionStorage.setItem(INTENDED_URL_KEY, currentPath);
      } catch (e) {
        // sessionStorage not available
      }
    }
  }
};

// Helper to get and clear intended URL
export const getAndClearIntendedUrl = (): string | null => {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    try {
      const url = sessionStorage.getItem(INTENDED_URL_KEY);
      sessionStorage.removeItem(INTENDED_URL_KEY);
      return url;
    } catch (e) {
      return null;
    }
  }
  return null;
};

export default function RootNavigator() {
  const { isAuthenticated, isLoading, accessDenial } = useAuth();

  // Save intended URL before SSO redirect happens. Skip when an access denial
  // is in effect — we don't want to bounce the user back to a page they can't
  // reach once they re-authenticate.
  useEffect(() => {
    if (!isLoading && !isAuthenticated && !accessDenial) {
      saveIntendedUrl();
    }
  }, [isLoading, isAuthenticated, accessDenial]);

  // If SSO has explicitly denied access, show the access-denied screen instead
  // of the loading spinner / SSO redirect loop.
  if (accessDenial) {
    return <AccessDeniedScreen />;
  }

  // Show loading spinner while checking auth / exchanging code
  if (isLoading || !isAuthenticated) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={COLORS.primary} />
      </View>
    );
  }

  return <MainNavigator />;
}

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: COLORS.background,
  },
});
